import omit from 'lodash/omit';
import {
  useTypedTranslate,
  UseTypedTranslateReturnType,
} from 'providers/i18n/hooks/useTypedTranslate';
import { Box } from 'shared/mui/Box';
import { Button } from 'shared/mui/Buttons';
import { CardActionsButtons } from 'shared/mui/Card';
import { CardContent } from 'shared/mui/Card/CardContent/CardContent';
import { CardHeader } from 'shared/mui/Card/CardHeader/CardHeader';
import { DialogProps } from 'shared/mui/Dialogs';
import { CloseIconButton } from 'shared/mui/IconButtons';
import { LinearProgress } from 'shared/mui/LinearProgress/LinearProgress';
import { Typography } from 'shared/mui/Typography';
import { getReportPeriod } from 'utils/getReportPeriod';

import { StyledBox, StyledDialog } from './OrdersListSummaryDialog.styled';
import { OrdersListSummaryDialogTable } from './OrdersListSummaryDialogTable';
import { useGetSummaryCopyData } from '../../hooks/useGetSummaryCopyData';
import { useGetTransactionsReportSummary } from '../../hooks/useGetTransactionsReportSummary';
import { OrdersListFilterFormValues } from '../OrdersListContent';

type Props = {
  filterValues: OrdersListFilterFormValues;
} & DialogProps;

const getDirectionName = (direction: string | null, translate: UseTypedTranslateReturnType) => {
  if (!direction) {
    return translate('app.direction.all');
  }

  return translate(`app.direction.${direction as 'payment' | 'payout' | 'all'}`);
};

export const OrdersListSummaryDialog: React.FC<Props> = ({ filterValues, open, onClose }) => {
  const translate = useTypedTranslate();

  const { data: summary, isLoading } = useGetTransactionsReportSummary({
    filters: omit(
      { ...filterValues, provider: filterValues['gateway.provider']! },
      'gateway.provider',
    ),
  });

  const { executedOrderCount = 0, notExecutedOrderCount = 0 } = summary || {};
  const total = Number(executedOrderCount) + Number(notExecutedOrderCount);

  const reportPeriod = getReportPeriod({
    dateGte: filterValues?.createdAt?.gte,
    dateLte: filterValues?.createdAt?.lte,
    timezone: filterValues?.timezone,
  });

  const { direction: directionFilterValue } = filterValues;
  const direction = getDirectionName(directionFilterValue, translate);

  const { handleCopy } = useGetSummaryCopyData({ summary, reportPeriod, direction });

  const periodMessage = translate('app.orders.ordersRegisteredInStatuses', {
    reportPeriod,
    direction,
  });

  return (
    <StyledDialog onClose={onClose} open={open}>
      <CardHeader
        appendActions={[<CloseIconButton key="close-button" onClose={onClose} />]}
        title={translate('app.orders.ordersSummaryForPeriod')}
        titleSize="smallBold"
      />
      <CardContent>
        {isLoading ? (
          <LinearProgress sx={{ margin: '16px auto' }} />
        ) : (
          <Box>
            <Typography>{periodMessage}</Typography>
            <OrdersListSummaryDialogTable summary={summary} />
            <StyledBox>
              <Typography sx={{ fontWeight: 500, textTransform: 'uppercase' }} variant="body1">
                {translate('app.orders.total')}: {total}
              </Typography>
              <Typography color="secondary" component="span" sx={{ marginRight: '16px' }}>
                {translate('app.orders.completed')}:{' '}
                <Typography color="text.dark" component="span">
                  {summary?.executedOrderCount}
                </Typography>
              </Typography>
              <Typography color="secondary" component="span">
                {translate('app.orders.notCompleted')}:{' '}
                <Typography color="text.dark" component="span">
                  {summary?.notExecutedOrderCount}
                </Typography>
              </Typography>
            </StyledBox>
          </Box>
        )}
      </CardContent>
      <CardActionsButtons
        rightActionsSlot={
          <Button
            label={translate('app.orders.copySummary')}
            onClick={handleCopy}
            variant="contained"
          />
        }
      />
    </StyledDialog>
  );
};
